// =============================================================================
// workspaces.js, Phase 31S.2 client for Summit workspace endpoints.
// =============================================================================
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const H = (token) => ({ Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' });

export async function listMyWorkspaces(token) {
  if (!token) return { workspaces: [] };
  try {
    const r = await axios.get(`${BACKEND_URL}/api/workspaces/me`, { headers: H(token), timeout: 8000 });
    return r.data;
  } catch (e) {
    console.warn('listMyWorkspaces failed:', e?.message);
    return { workspaces: [] };
  }
}

export async function listMembers(token, workspaceId) {
  try {
    const r = await axios.get(`${BACKEND_URL}/api/workspaces/${workspaceId}/members`, { headers: H(token), timeout: 8000 });
    return r.data;
  } catch (e) {
    console.warn('listMembers failed:', e?.message);
    return { members: [] };
  }
}

export async function inviteMember(token, workspaceId, email, role = 'partner') {
  try {
    const r = await axios.post(`${BACKEND_URL}/api/workspaces/${workspaceId}/invite`,
      { email, role }, { headers: H(token), timeout: 10000 });
    return r.data;
  } catch (e) {
    return { error: e?.response?.data?.detail || e?.message || 'Invite failed.',
             code: e?.response?.status };
  }
}

export async function acceptInvite(token, { token: inviteToken, code }) {
  try {
    const r = await axios.post(`${BACKEND_URL}/api/workspaces/accept`,
      { token: inviteToken, code }, { headers: H(token), timeout: 10000 });
    return r.data;
  } catch (e) {
    return { error: e?.response?.data?.detail || e?.message || 'Could not accept invite.',
             code: e?.response?.status };
  }
}

export async function removeMember(token, workspaceId, memberId) {
  try {
    const r = await axios.delete(`${BACKEND_URL}/api/workspaces/${workspaceId}/members/${memberId}`,
      { headers: H(token), timeout: 8000 });
    return r.data;
  } catch (e) {
    return { error: e?.response?.data?.detail || e?.message || 'Could not remove member.' };
  }
}

export async function getAuditLog(token, workspaceId, limit = 50) {
  try {
    const r = await axios.get(`${BACKEND_URL}/api/workspaces/${workspaceId}/audit?limit=${limit}`,
      { headers: H(token), timeout: 8000 });
    return r.data;
  } catch (e) {
    return { entries: [] };
  }
}

export async function flagTransaction(token, txnId, note = null, clear = false) {
  try {
    const r = await axios.post(`${BACKEND_URL}/api/transactions/${txnId}/flag`,
      { note, clear }, { headers: H(token), timeout: 8000 });
    return r.data;
  } catch (e) {
    return { error: e?.response?.data?.detail || e?.message || 'Could not flag right now.' };
  }
}

// ---------------------------------------------------------------------------
// hasActivePartner, true iff the current user has at least one workspace
// where they share with another accepted member (i.e. a real "partner" to
// flag things for). Used to gate the flag-for-review affordance: without a
// partner, the flag button instead opens a calm "invite a partner first"
// dialog rather than silently flagging into the void.
// ---------------------------------------------------------------------------
export async function hasActivePartner(token) {
  if (!token) return false;
  try {
    const { workspaces = [] } = await listMyWorkspaces(token);
    if (!workspaces.length) return false;

    // Fast path: backend returns `member_count` per workspace. If any
    // workspace has > 1 active member, the user has a partner. If ALL
    // workspaces are solo (member_count <= 1), short-circuit to false  
    // no need to probe the members endpoint.
    const counts = workspaces.map((w) => (typeof w?.member_count === 'number' ? w.member_count : null));
    if (counts.every((c) => c != null)) {
      return counts.some((c) => c > 1);
    }
    if (counts.some((c) => c != null && c > 1)) return true;

    // Some rows are missing member_count, fall back to listing members of
    // the first workspace (covers older deployments / cache misses).
    const first = workspaces[0];
    if (!first?.id) return false;
    const { members = [] } = await listMembers(token, first.id);
    return (members || []).length > 1;
  } catch (e) {
    return false;
  }
}
